// src/pages/industries/IndustryPageTemplate.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import Contact from '../../components/Contact';
import WhatsAppButton from '../../components/WhatsAppButton';
import VideoTestimonials from '../../components/VideoTestimonials';
import CTA from '../../components/CTA';
import StarBackground from '../../components/StarBackground';
import { useSiteContent } from '../../context/SiteContentContext';

import {
  FiArrowRight, FiCheck, FiStar, FiAward, FiClock, FiShield,
  FiZap, FiLayers, FiHelpCircle, FiChevronDown, FiChevronUp,
  FiCheckCircle, FiActivity, FiHome, FiShoppingCart, FiBookOpen,
  FiTrendingUp, FiLock, FiCpu, FiMonitor, FiDatabase, FiServer,
  FiGlobe, FiSmartphone, FiBarChart2, FiSend
} from 'react-icons/fi';
import { IoRestaurantOutline } from 'react-icons/io5';
import { MdFitnessCenter } from 'react-icons/md';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Custom Icon Mapper
const iconMap = {
  '🏥': <FiActivity />,
  '💪': <MdFitnessCenter />,
  '🏠': <FiHome />,
  '🏢': <FiHome />,
  '🛒': <FiShoppingCart />,
  '🍔': <IoRestaurantOutline />,
  '🤝': <FiAward />,
  '🎓': <FiBookOpen />,
  '⭐': <FiStar />,
  '🏆': <FiAward />,
  '⚡': <FiZap />,
  '📊': <FiTrendingUp />,
  '🔒': <FiLock />,
  '🖥️': <FiMonitor />,
  '🤖': <FiCpu />,
};

const getIcon = (emoji, className = '') => {
  const icon = iconMap[emoji] || <FiStar />;
  return React.cloneElement(icon, { className: `${className} flex-shrink-0` });
};

// Generic Industry FAQs generator
const getIndustryFaqs = (badge) => [
  {
    q: `How long does it take to develop a custom ${badge || 'Industry'} software platform?`,
    a: `Development timelines typically range between 4 to 8 weeks for a feature-complete Minimum Viable Product (MVP), depending on integration scope, custom database workflows, and mobile application requirements.`
  },
  {
    q: `Is the platform fully HIPAA / PCI-DSS / Data Privacy compliant?`,
    a: `Yes! Every platform we architect adheres to strict global data security standards, including encrypted database storage, secure SSL endpoints, role-based access control (RBAC), and automated compliance backups.`
  },
  {
    q: `Can you integrate our new portal with existing database software?`,
    a: `Absolutely. We design custom REST and GraphQL API adapters to seamlessly bridge legacy databases, EHR/EMR platforms, payment gateways, and third-party CRM systems.`
  },
  {
    q: `Will the application work on both Desktop and Mobile devices?`,
    a: `Yes! All our web portals and Progressive Web Applications (PWAs) are 100% responsive, optimized for desktop browsers, tablets, and iOS/Android mobile screens with sub-second page loads.`
  },
  {
    q: `What support and maintenance do you provide post-launch?`,
    a: `We offer 24/7 system monitoring, security patch updates, server optimization, and continuous feature updates to ensure your platform runs smoothly at scale.`
  }
];

// Industry Pain Points vs Solutions Generator
const getIndustryPainPoints = (badge) => [
  {
    problem: `Manual scheduling & phone-based booking delays in ${badge || 'operations'}`,
    solution: "24/7 Automated self-service calendar & dispatch engine"
  },
  {
    problem: "Data scattered across legacy spreadsheets & paper files",
    solution: "Centralized cloud database with real-time analytics"
  },
  {
    problem: "High dropoff rates on outdated mobile checkout flows",
    solution: "Ultra-fast mobile-optimized UI/UX with 1-click POS billing"
  },
  {
    problem: "Lack of automated customer follow-ups & SMS alerts",
    solution: "Instant SMS/email push triggers for reminders & updates"
  }
];

// Tech Stack List
const techStack = [
  { name: 'React 18 / Next.js', role: 'Frontend & PWA', icon: <FiGlobe className="text-[#F2B135]" /> },
  { name: 'Node.js & Python', role: 'Backend API Engine', icon: <FiServer className="text-[#F2B135]" /> },
  { name: 'PostgreSQL / Mongo', role: 'Encrypted Database', icon: <FiDatabase className="text-[#F2B135]" /> },
  { name: 'AWS & Cloudflare', role: 'Cloud Infrastructure', icon: <FiCpu className="text-[#F2B135]" /> },
  { name: 'Docker & Kubernetes', role: 'Container Scaling', icon: <FiLayers className="text-[#F2B135]" /> },
  { name: 'Stripe & Square POS', role: 'Secure Payments', icon: <FiShield className="text-[#F2B135]" /> },
];

// Case Studies Scenarios Generator
const getCaseScenarios = (badge) => [
  {
    title: `Enterprise ${badge || 'System'} Modernization`,
    result: "85% Reduction in Admin Processing Time",
    desc: `Automated patient and client registration workflows with instant mobile check-ins and centralized cloud CRM reporting.`
  },
  {
    title: "High-Volume Mobile Booking App",
    result: "4.2x Growth in Direct Online Bookings",
    desc: "Shipped an iOS & Android PWA featuring live slot selection, automated reminders, and Stripe payment gateway integration."
  },
  {
    title: "Real-Time Field Dispatch & Analytics",
    result: "99.9% SLA Uptime & Sub-Second Latency",
    desc: "Connected field staff, management dashboards, and client tracking portals through live WebSocket data synchronization."
  }
];

const IndustryPageTemplate = ({ pageData }) => {
  const { siteContent } = useSiteContent();
  const currentPath = window.location.pathname;

  const serviceKeyMap = {
    '/industries/healthcare': 'ind-healthcare',
    '/industries/fitness': 'ind-fitness',
    '/industries/home-services': 'ind-home-svc',
    '/industries/real-estate': 'ind-real-estate',
    '/industries/ecommerce': 'ind-ecommerce',
    '/industries/restaurant': 'ind-restaurant',
    '/industries/coaching': 'ind-coaching',
    '/industries/education': 'ind-education'
  };

  const dbKey = serviceKeyMap[currentPath];
  const dbContent = dbKey && siteContent ? siteContent[dbKey] : null;

  const { seo, hero, showcase, videoSection, cards, features } = pageData;

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [consultEmail, setConsultEmail] = useState('');
  const [consultSubmitted, setConsultSubmitted] = useState(false);

  const sectionRef = useRef(null);
  const mockupRef = useRef(null);

  const slide = hero?.slides?.[activeSlideIndex] || hero?.slides?.[0] || {};
  const faqs = getIndustryFaqs(hero?.badge);
  const painPoints = getIndustryPainPoints(hero?.badge);
  const caseScenarios = getCaseScenarios(hero?.badge);

  // Auto rotate hero slides if multiple exist
  useEffect(() => {
    if (!hero?.slides || hero.slides.length <= 1) return;
    const interval = setInterval(() => {
      setActiveSlideIndex((prev) => (prev + 1) % hero.slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [hero?.slides]);

  // Update document title for SEO
  useEffect(() => {
    if (seo?.title) document.title = seo.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && seo?.description) {
      metaDescription.setAttribute('content', seo.description);
    }
  }, [seo]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleConsultSubmit = (e) => {
    e.preventDefault();
    if (consultEmail.trim()) {
      setConsultSubmitted(true);
      setTimeout(() => {
        setConsultSubmitted(false);
        setConsultEmail('');
      }, 4000);
    }
  };

  return (
    <div className="relative min-h-screen bg-black overflow-hidden font-sans text-white select-none">
      <StarBackground />
      <Navbar />
      <WhatsAppButton />

      {/* ═══════════════════════════════════════════════════════════
          SIGNATURE HERO SECTION (FULL SCREEN HEIGHT FOR VIDEO)
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen w-full flex items-center justify-center pt-32 pb-24 overflow-hidden">
        {/* Full Height Background Video */}
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-100">
            <source src="/herovideo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full pt-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* LEFT COLUMN CONTENT */}
            <div className="flex flex-col items-start text-left">
              {/* Badge Pill */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/35 bg-[#F2B135]/15 text-[#F2B135] text-xs font-bold uppercase mb-6 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
                ROJRZTECH INDUSTRY • {hero.badge}
              </div>

              {/* Title */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight font-display"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {dbContent?.title ? dbContent.title : (
                  <>
                    {slide.heading?.pre && <>{slide.heading.pre} </>}
                    {slide.heading?.gold && (
                      <span className="text-[#F2B135] drop-shadow-[0_0_20px_rgba(242,177,53,0.35)]">
                        {slide.heading.gold}
                      </span>
                    )}
                    {slide.heading?.post && <> {slide.heading.post}</>}
                  </>
                )}
              </h1>

              {/* Subheading */}
              <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-xl font-medium">
                {dbContent?.subtitle || slide.subheading}
              </p>

              {/* Pill Tags */}
              <div className="flex flex-wrap gap-2 mb-10">
                {slide.pills?.map((pill, i) => (
                  <span
                    key={i}
                    className="px-3.5 py-1.5 rounded-full bg-white/5 border border-[#F2B135]/30 text-white text-xs font-extrabold backdrop-blur-sm hover:border-[#F2B135] hover:bg-[#F2B135]/10 transition-all cursor-default"
                  >
                    ✓ {pill}
                  </span>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Link to="/contact" className="btn-animated-gold text-center">
                  Schedule Strategy Meeting <FiArrowRight size={18} />
                </Link>
                <a
                  href="#showcase"
                  className="flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white border border-white/20 hover:bg-white/10 hover:border-[#F2B135] transition-all text-base font-semibold"
                >
                  Explore Platform Features
                </a>
              </div>
            </div>

            {/* RIGHT COLUMN SHOWCASE IMAGE */}
            <div className="relative mt-8 lg:mt-0 flex justify-center lg:justify-end">
              <div className="relative rounded-3xl overflow-hidden border border-[#F2B135]/30 shadow-[0_20px_60px_rgba(0,0,0,0.9),0_0_50px_rgba(242,177,53,0.2)] max-w-lg w-full group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#F2B135]/25 to-[#ffaa00]/15 rounded-[32px] blur-xl opacity-40 group-hover:opacity-75 transition-opacity duration-300" />
                <img
                  src={slide.image}
                  alt={hero.badge}
                  className="w-full object-cover rounded-3xl min-h-[340px] max-h-[480px] transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                
                {/* Image Overlay Banner */}
                <div className="absolute bottom-6 left-6 right-6 p-4 rounded-2xl bg-black/80 backdrop-blur-md border border-[#F2B135]/30 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F2B135] text-black font-black flex items-center justify-center text-lg">
                      ★
                    </div>
                    <div>
                      <div className="text-white font-bold text-sm">Enterprise Ready</div>
                      <div className="text-white/60 text-xs">{hero.badge} Platform</div>
                    </div>
                  </div>
                  <span className="text-xs text-[#F2B135] font-black uppercase tracking-wider">
                    100% Scalable
                  </span>
                </div>
              </div>

              {/* Floating Rating Badge */}
              <div className="absolute -top-4 -right-2 hidden sm:flex bg-black/95 backdrop-blur-xl border border-[#F2B135]/50 rounded-2xl p-4 items-center gap-3 shadow-2xl z-30">
                <FiStar className="text-[#F2B135] fill-[#F2B135]" size={24} />
                <div>
                  <div className="text-white font-black text-sm">4.9/5 Rating</div>
                  <div className="text-white/60 text-[11px] font-medium">Industry Benchmark</div>
                </div>
              </div>
            </div>

          </div>

          {/* STATS COUNTER BAR */}
          <div className="mt-16 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
              <div className="text-2xl sm:text-3xl font-black text-[#F2B135] font-display">50+</div>
              <div className="text-white/60 text-xs font-semibold uppercase mt-1">Systems Deployed</div>
            </div>
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
              <div className="text-2xl sm:text-3xl font-black text-white font-display">99.9%</div>
              <div className="text-white/60 text-xs font-semibold uppercase mt-1">Uptime Guaranteed</div>
            </div>
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
              <div className="text-2xl sm:text-3xl font-black text-[#F2B135] font-display">10x</div>
              <div className="text-white/60 text-xs font-semibold uppercase mt-1">Lead Conversion ROI</div>
            </div>
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5">
              <div className="text-2xl sm:text-3xl font-black text-white font-display">24/7</div>
              <div className="text-white/60 text-xs font-semibold uppercase mt-1">Dedicated Support</div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          INTERACTIVE PHONE SHOWCASE SECTION
      ═══════════════════════════════════════════════════════════ */}
      <section id="showcase" ref={sectionRef} className="py-24 relative bg-transparent overflow-hidden z-10">
        <div className="container-custom relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/15 text-[#F2B135] text-xs font-black uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F2B135] animate-ping" />
              Interactive Platform Showcase
            </div>
            <h2
              className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight font-display"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Tailored Digital Solutions for <span className="text-[#F2B135]">{hero.badge}</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg font-medium leading-relaxed">
              Explore how our integrated platforms connect your services, automate workflows, and drive user engagement seamlessly across web &amp; mobile interfaces.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center relative">
            {/* Left 3 Floating Tags — hidden on mobile, show on lg */}
            <div className="hidden lg:flex lg:col-span-4 flex-col gap-5 justify-center order-2 lg:order-1">
              {showcase.tags.slice(0, 3).map((tag, idx) => (
                <div
                  key={idx}
                  className="bg-zinc-950/90 border border-[#F2B135]/30 hover:border-[#F2B135] rounded-2xl p-5 flex items-start gap-4 shadow-xl hover:bg-[#F2B135]/5 transition-all duration-300"
                >
                  <div className="w-9 h-9 rounded-full bg-[#F2B135] flex items-center justify-center text-black flex-shrink-0 font-black">
                    <FiCheck size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-white font-extrabold text-sm mb-1 uppercase tracking-wider font-display">{tag.title}</h4>
                    <p className="text-white/60 text-xs leading-relaxed font-medium">{tag.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Center Phone Mockup */}
            <div className="lg:col-span-4 flex items-center justify-center relative min-h-[460px] order-1 lg:order-2">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] rounded-full border-2 border-dashed border-[#F2B135]/20 animate-[spin_40s_linear_infinite]" />
                <div className="w-[360px] h-[360px] sm:w-[460px] sm:h-[460px] rounded-full border border-dotted border-[#F2B135]/15 animate-[spin_25s_linear_infinite_reverse]" />
              </div>

              <div
                ref={mockupRef}
                className="relative w-[270px] h-[520px] sm:w-[290px] sm:h-[570px] bg-black rounded-[3rem] border-[10px] border-zinc-900 shadow-[0_25px_60px_rgba(0,0,0,0.9),0_0_40px_rgba(242,177,53,0.2)] overflow-hidden z-10 flex flex-col"
              >
                <div className="absolute inset-0 w-full h-full flex flex-col bg-zinc-950">
                  <div className="w-28 h-5 bg-black rounded-full mx-auto mt-3 mb-2 relative z-50 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-zinc-800 absolute right-4" />
                  </div>

                  <div className="px-4 py-2 border-b border-[#F2B135]/25 flex items-center justify-between">
                    <span className="text-[10px] font-black text-[#F2B135] tracking-widest uppercase font-display">
                      ROJRZTECH SYSTEM
                    </span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F2B135] animate-ping" />
                  </div>

                  <div className="flex-1 p-4 overflow-y-auto space-y-4 select-none">
                    <div className="rounded-xl border border-[#F2B135]/30 bg-gradient-to-br from-[#F2B135]/20 to-transparent p-3.5 text-center">
                      <h5 className="text-[11px] font-extrabold text-[#F2B135] uppercase mb-1">{showcase.phoneTitle}</h5>
                      <p className="text-[9px] text-white/80 leading-normal font-medium">{showcase.phoneSubtitle}</p>
                    </div>

                    <div className="space-y-2">
                      <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Active System Modules</span>
                      {showcase.tags.map((tag, tIndex) => (
                        <div key={tIndex} className="rounded-lg bg-zinc-900/90 border border-white/10 p-2.5 flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-[#F2B135]/25 text-[#F2B135] flex items-center justify-center text-[9px] font-bold">
                            ✓
                          </div>
                          <span className="text-[9.5px] font-black text-white uppercase">{tag.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-3 border-t border-[#F2B135]/25 flex justify-around text-[9px] font-extrabold text-white">
                    <span className="text-[#F2B135]">PORTAL</span>
                    <span>ANALYTICS</span>
                    <span>SUPPORT</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right 3 Floating Tags — hidden on mobile, show on lg */}
            <div className="hidden lg:flex lg:col-span-4 flex-col gap-5 justify-center order-3">
              {showcase.tags.slice(3, 6).map((tag, idx) => (
                <div
                  key={idx}
                  className="bg-zinc-950/90 border border-[#F2B135]/30 hover:border-[#F2B135] rounded-2xl p-5 flex items-start gap-4 shadow-xl hover:bg-[#F2B135]/5 transition-all duration-300"
                >
                  <div className="w-9 h-9 rounded-full bg-[#F2B135] flex items-center justify-center text-black flex-shrink-0 font-black">
                    <FiCheck size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-white font-extrabold text-sm mb-1 uppercase tracking-wider font-display">{tag.title}</h4>
                    <p className="text-white/60 text-xs leading-relaxed font-medium">{tag.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* All 6 Tags Grid — visible only on mobile */}
            <div className="lg:hidden col-span-12 order-2 grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {showcase.tags.map((tag, idx) => (
                <div
                  key={idx}
                  className="bg-zinc-950/90 border border-[#F2B135]/30 rounded-2xl p-4 flex items-start gap-3 shadow-xl"
                >
                  <div className="w-8 h-8 rounded-full bg-[#F2B135] flex items-center justify-center text-black flex-shrink-0 font-black">
                    <FiCheck size={14} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="text-white font-extrabold text-xs mb-0.5 uppercase tracking-wider">{tag.title}</h4>
                    <p className="text-white/60 text-[11px] leading-relaxed">{tag.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          PAIN POINTS VS ROJRZTECH SOLUTIONS MATRIX
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 relative bg-transparent z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
              Why Upgrade Your Stack
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Eliminate Industry Friction &amp; Scale Revenue
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Legacy Bottlenecks */}
            <div className="p-8 rounded-3xl bg-red-950/10 border border-red-500/20 backdrop-blur-md">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 text-red-400 font-black flex items-center justify-center text-xl">
                  ✕
                </div>
                <h3 className="text-xl font-bold text-red-200 font-display">Traditional Industry Bottlenecks</h3>
              </div>
              <ul className="space-y-4">
                {painPoints.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70 text-sm font-medium">
                    <span className="text-red-400 font-bold mt-0.5">✕</span>
                    <span>{item.problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* RojrzTech Advantage */}
            <div className="p-8 rounded-3xl bg-[#F2B135]/10 border border-[#F2B135]/40 backdrop-blur-md shadow-[0_0_30px_rgba(242,177,53,0.08)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#F2B135] text-black font-black flex items-center justify-center text-xl">
                  ✓
                </div>
                <h3 className="text-xl font-bold text-[#F2B135] font-display">RojrzTech Digital Advantage</h3>
              </div>
              <ul className="space-y-4">
                {painPoints.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white text-sm font-semibold">
                    <span className="text-[#F2B135] font-bold mt-0.5">✓</span>
                    <span>{item.solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CORE COMPETENCIES & SERVICES CARDS
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/15 text-[#F2B135] text-xs font-black uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F2B135]" />
              Core Competencies
            </div>
            <h2
              className="text-3xl md:text-5xl font-black text-white mb-6 font-display"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {cards.title}
            </h2>
            <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto font-medium">
              {cards.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.items.map((card, i) => (
              <div
                key={i}
                className="group bg-[#0d0d0f]/90 border border-white/10 hover:border-[#F2B135]/50 rounded-2xl p-8 hover:bg-[#F2B135]/5 hover:-translate-y-2 transition-all duration-300 text-left shadow-2xl flex flex-col justify-between"
              >
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-[#F2B135]/10 border border-[#F2B135]/30 flex items-center justify-center text-[#F2B135] text-3xl mb-6 group-hover:bg-[#F2B135] group-hover:text-black group-hover:scale-110 transition-all duration-300">
                    {getIcon(card.icon, "w-7 h-7")}
                  </div>
                  <h3 className="text-xl font-extrabold text-white mb-4 group-hover:text-[#F2B135] transition-colors leading-snug font-display">
                    {card.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed font-medium">
                    {card.description}
                  </p>
                </div>

                <div className="mt-8 pt-4 flex items-center justify-between text-xs font-bold text-[#F2B135]">
                  <span>Explore Module</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          NEW RELEVANT SECTION 1: TECH STACK & CLOUD ECOSYSTEM
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 relative bg-transparent z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
              Enterprise Architecture
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built With High-Performance Tech Stack
            </h2>
            <p className="text-white/70 text-base md:text-lg font-medium mt-4">
              We leverage enterprise software frameworks to ensure maximum reliability, security, and sub-second render speeds.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {techStack.map((tech, tIdx) => (
              <div
                key={tIdx}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#F2B135]/50 hover:bg-[#F2B135]/5 transition-all text-center flex flex-col items-center justify-center group"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>
                <h4 className="text-white font-bold text-sm font-display mb-1">{tech.name}</h4>
                <span className="text-white/50 text-[11px] font-medium">{tech.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          VIDEO DEMONSTRATION SECTION
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="container-custom max-w-5xl mx-auto px-6">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/15 text-[#F2B135] text-xs font-black uppercase mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F2B135]" />
              Video Demonstration
            </div>
            <h2
              className="text-3xl md:text-5xl font-black text-white mb-4 font-display"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {videoSection.title}
            </h2>
            <p className="text-white/70 text-base md:text-lg font-medium">
              {videoSection.subtitle}
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden border border-[#F2B135]/30 shadow-[0_20px_50px_rgba(0,0,0,0.9),0_0_30px_rgba(242,177,53,0.15)] group">
            <div className="relative aspect-video w-full bg-black">
              <video
                src="/herovideo.mp4"
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                autoPlay
                muted
                loop
                playsInline
              />
              <div className="absolute bottom-6 left-6 bg-black/80 backdrop-blur-md border border-[#F2B135]/30 rounded-xl px-5 py-3 z-20 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-[#F2B135] animate-pulse" />
                <span className="text-white text-xs font-black tracking-widest uppercase">
                  {videoSection.frameLabel || 'LIVE SYSTEM DEMO'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          NEW RELEVANT SECTION 2: CASE SCENARIOS & SUCCESS METRICS
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
              Proven Results
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Real-World Impact &amp; Success Scenarios
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {caseScenarios.map((cs, cIdx) => (
              <div
                key={cIdx}
                className="p-8 rounded-3xl bg-[#0d0d0f] border border-white/10 hover:border-[#F2B135]/50 transition-all flex flex-col justify-between group hover:-translate-y-1"
              >
                <div>
                  <div className="text-xs font-bold text-[#F2B135] uppercase tracking-widest mb-3">Case Scenario #{cIdx + 1}</div>
                  <h3 className="text-xl font-bold text-white mb-3 font-display">{cs.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-6 font-medium">{cs.desc}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F2B135]/10 border border-[#F2B135]/30">
                  <span className="text-xs font-bold text-white/50 block uppercase">Key Outcome:</span>
                  <span className="text-base font-black text-[#F2B135] font-display">{cs.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          FEATURES CHECKLIST GRID (12 POINTS)
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2
              className="text-3xl md:text-5xl font-black text-white mb-6 font-display"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {features.title}
            </h2>
            <p className="text-white/70 text-base md:text-lg font-medium">
              {features.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {features.items.map((item, i) => (
              <div
                key={i}
                className="border border-[#F2B135]/25 rounded-2xl px-5 py-4 flex items-center gap-3.5 bg-zinc-950/80 hover:border-[#F2B135] hover:bg-[#F2B135]/10 transition-all duration-300 group cursor-default"
              >
                <div className="w-6 h-6 rounded-full bg-[#F2B135]/15 border border-[#F2B135]/40 flex items-center justify-center text-[#F2B135] flex-shrink-0 group-hover:bg-[#F2B135] group-hover:text-black transition-colors">
                  <FiCheck size={14} strokeWidth={3} />
                </div>
                <span className="text-white text-xs md:text-sm font-bold leading-snug">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          OUR 4-STEP DEVELOPMENT PROCESS TIMELINE
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
              Execution Methodology
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How We Deliver Your Industry Platform
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            {[
              { step: '01', title: 'Discovery & Scope', desc: 'Detailed business goal mapping, database schema design, and technical requirements documentation.' },
              { step: '02', title: 'UI/UX & Wireframes', desc: 'Designing intuitive wireframes and interactive prototypes optimized for conversion.' },
              { step: '03', title: 'Agile Development', desc: 'Writing modular code, implementing API microservices, and conducting automated unit testing.' },
              { step: '04', title: 'Deployment & Scale', desc: 'Zero-downtime cloud release, continuous monitoring, and ongoing 24/7 technical optimization.' }
            ].map((p, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-[#F2B135]/50 transition-all duration-300 relative">
                <div className="w-12 h-12 rounded-xl bg-[#F2B135]/15 border border-[#F2B135]/40 text-[#F2B135] font-black text-xl flex items-center justify-center mb-4 font-display">
                  {p.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 font-display">{p.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed font-medium">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ═══════════════════════════════════════════════════════════
          WHY CHOOSE ROJRZTECH (4 PILLARS)
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 relative bg-transparent z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
              Enterprise Assurance
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built for Scale, Speed &amp; Security
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-[#0d0d0f] border border-white/10 hover:border-[#F2B135]/40 transition-all">
              <FiLock className="text-[#F2B135] text-3xl mb-4" />
              <h4 className="text-white font-bold text-base mb-2 font-display">Enterprise Security</h4>
              <p className="text-white/60 text-xs leading-relaxed font-medium">HIPAA, SOC2, and SSL encrypted architecture guarding patient and client data.</p>
            </div>
            <div className="p-6 rounded-2xl bg-[#0d0d0f] border border-white/10 hover:border-[#F2B135]/40 transition-all">
              <FiZap className="text-[#F2B135] text-3xl mb-4" />
              <h4 className="text-white font-bold text-base mb-2 font-display">Lightning Performance</h4>
              <p className="text-white/60 text-xs leading-relaxed font-medium">Sub-second page rendering and lightweight codebases built for high traffic.</p>
            </div>
            <div className="p-6 rounded-2xl bg-[#0d0d0f] border border-white/10 hover:border-[#F2B135]/40 transition-all">
              <FiLayers className="text-[#F2B135] text-3xl mb-4" />
              <h4 className="text-white font-bold text-base mb-2 font-display">Tailored Workflows</h4>
              <p className="text-white/60 text-xs leading-relaxed font-medium">Zero cookie-cutter solutions; every component is built for your business domain.</p>
            </div>
            <div className="p-6 rounded-2xl bg-[#0d0d0f] border border-white/10 hover:border-[#F2B135]/40 transition-all">
              <FiShield className="text-[#F2B135] text-3xl mb-4" />
              <h4 className="text-white font-bold text-base mb-2 font-display">24/7 Dedicated Uptime</h4>
              <p className="text-white/60 text-xs leading-relaxed font-medium">Continuous server monitoring and proactive support teams ensuring uninterrupted operations.</p>
            </div>
          </div>
        </div>
      </section>



      {/* ═══════════════════════════════════════════════════════════
          NEW SECTION: ANIMATED STATS COUNTER STRIP
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 relative bg-transparent z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Animated glow bar */}
          <div className="relative rounded-3xl border border-[#F2B135]/30 bg-gradient-to-r from-[#F2B135]/5 via-transparent to-[#F2B135]/5 p-8 md:p-12 overflow-hidden">
            {/* Animated background pulse */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#F2B135]/0 via-[#F2B135]/5 to-[#F2B135]/0 animate-[pulse_3s_ease-in-out_infinite]" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-40 bg-[#F2B135]/10 rounded-full blur-3xl" />

            <div className="relative z-10">
              <div className="text-center mb-10">
                <span className="text-xs font-black text-[#F2B135] uppercase tracking-widest font-display block mb-2">Proven ROI Across Industries</span>
                <h2 className="text-2xl md:text-4xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Numbers That <span className="text-[#F2B135]">Speak for Themselves</span>
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { value: '10x', label: 'Revenue Growth', sub: 'Average client ROI', icon: '📈' },
                  { value: '85%', label: 'Admin Time Saved', sub: 'Via automation', icon: '⚡' },
                  { value: '500+', label: 'Projects Shipped', sub: 'Across 8 industries', icon: '🚀' },
                  { value: '4.9★', label: 'Client Rating', sub: 'Google & Clutch verified', icon: '⭐' },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="text-center group"
                  >
                    <div className="text-3xl mb-2">{stat.icon}</div>
                    <div
                      className="text-3xl md:text-5xl font-black text-[#F2B135] font-display mb-1"
                      style={{ fontFamily: 'Outfit, sans-serif', textShadow: '0 0 30px rgba(242,177,53,0.4)' }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-white font-bold text-sm mb-0.5">{stat.label}</div>
                    <div className="text-white/50 text-xs font-medium">{stat.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          NEW SECTION: TRUSTED BY / PARTNER TECH MARQUEE
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 relative bg-transparent z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-black text-[#F2B135] uppercase tracking-widest font-display block mb-2">Trusted Technologies</span>
            <h2 className="text-2xl md:text-3xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Powered by <span className="text-[#F2B135]">World-Class Tools</span>
            </h2>
          </div>

          {/* Marquee Container */}
          <div className="relative overflow-hidden">
            {/* Left fade */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-black to-transparent z-10" />
            {/* Right fade */}
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-black to-transparent z-10" />

            <div className="flex gap-6 animate-marquee" style={{ width: 'max-content' }}>
              {[
                { name: 'React', icon: '⚛️' },
                { name: 'Next.js', icon: '▲' },
                { name: 'Node.js', icon: '🟢' },
                { name: 'AWS', icon: '☁️' },
                { name: 'Stripe', icon: '💳' },
                { name: 'MongoDB', icon: '🍃' },
                { name: 'PostgreSQL', icon: '🐘' },
                { name: 'Docker', icon: '🐳' },
                { name: 'Cloudflare', icon: '🔥' },
                { name: 'Figma', icon: '🎨' },
                { name: 'Python', icon: '🐍' },
                { name: 'Shopify', icon: '🛒' },
                { name: 'WordPress', icon: '📝' },
                { name: 'Tailwind', icon: '💨' },
                // Duplicate for seamless loop
                { name: 'React', icon: '⚛️' },
                { name: 'Next.js', icon: '▲' },
                { name: 'Node.js', icon: '🟢' },
                { name: 'AWS', icon: '☁️' },
                { name: 'Stripe', icon: '💳' },
                { name: 'MongoDB', icon: '🍃' },
                { name: 'PostgreSQL', icon: '🐘' },
                { name: 'Docker', icon: '🐳' },
                { name: 'Cloudflare', icon: '🔥' },
                { name: 'Figma', icon: '🎨' },
                { name: 'Python', icon: '🐍' },
                { name: 'Shopify', icon: '🛒' },
                { name: 'WordPress', icon: '📝' },
                { name: 'Tailwind', icon: '💨' },
              ].map((tech, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#F2B135]/40 transition-all duration-300 cursor-default flex-shrink-0"
                >
                  <span className="text-xl">{tech.icon}</span>
                  <span className="text-white font-bold text-sm whitespace-nowrap">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          NEW SECTION: FREE QUOTE ANIMATED CTA BANNER
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 relative bg-transparent z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            {/* Animated border gradient */}
            <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-r from-[#F2B135] via-[#ffcc6b] to-[#F2B135] animate-[spin_4s_linear_infinite] opacity-70" style={{ backgroundSize: '200% 200%', animation: 'gradientShift 3s ease infinite' }} />
            <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-r from-[#F2B135]/60 via-white/20 to-[#F2B135]/60" />

            <div className="relative bg-black rounded-3xl p-8 md:p-14 text-center">
              {/* Glow orb */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-32 bg-[#F2B135]/20 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F2B135]/40 bg-[#F2B135]/10 text-[#F2B135] text-xs font-black uppercase mb-6">
                  <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-ping" />
                  Free Strategy Session — Limited Slots
                </div>

                <h2
                  className="text-3xl md:text-5xl font-black text-white mb-4 font-display"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Ready to Build Your <span className="text-[#F2B135]">Custom {hero.badge} Platform?</span>
                </h2>

                <p className="text-white/70 text-base md:text-lg font-medium mb-8 max-w-2xl mx-auto">
                  Get a free 30-minute consultation and detailed project estimate with our senior engineers — zero obligation.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a
                    href="/contact"
                    className="btn-animated-gold text-center inline-flex items-center gap-2"
                  >
                    Get Free Quote <FiArrowRight size={18} />
                  </a>
                  <a
                    href="tel:+15122544193"
                    className="flex items-center gap-2 text-white/70 hover:text-[#F2B135] font-semibold text-sm transition-colors"
                  >
                    <FiSmartphone size={16} />
                    +1 (512) 254-4193
                  </a>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap justify-center gap-6 mt-8">
                  {['✓ No Hidden Fees', '✓ 48-Hour Turnaround', '✓ 100% Money Back', '✓ Dedicated Manager'].map((badge, i) => (
                    <span key={i} className="text-xs font-bold text-white/50">{badge}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          REUSED COMPONENTS
      ═══════════════════════════════════════════════════════════ */}
      <VideoTestimonials />
      <Contact />
      <CTA />
      <Footer />
    </div>
  );
};

export default IndustryPageTemplate;
